import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { Input } from '@/app/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { useElementErrorMsg, useFetch } from '@/app/hooks';
import { cn } from '@/app/utils';
import { materialsCreateLabel, materialsListLabels } from '../apis';
import type { MaterialsListLabelsData, MaterialV1Label } from '../types';
import { TagItem } from './Tag';

const TAG_NAME_MAX_LENGTH = 15;

type TagSelectProps = {
  options?: MaterialV1Label[];
  value?: string;
  onChange?: (id: string) => void;
  onAdd?: (label: MaterialV1Label) => void;
  placeholder?: string;
};

export const ChooseLabelSelect = React.memo<TagSelectProps>(({ options, onChange, value, onAdd, placeholder }) => {
  const t = useTranslations('elements');
  const ref = useRef<HTMLInputElement>(null);
  const { showErrorMsg } = useElementErrorMsg();
  const isComposing = useRef(false);
  const [open, setOpen] = useState(false);
  const [inputVal, setInputVal] = useState('');
  const {
    loading: labelLoading,
    data: labels,
    run: getLabels,
    mutate,
  } = useFetch(
    (params: Omit<MaterialsListLabelsData, 'url'>) => {
      return materialsListLabels(params);
    },
    {
      debounceWait: 200,
      manual: true,
      onSuccess: (data) => {
        if (data.error) {
          showErrorMsg(data.error);
        }
      },
      throttleTrailing: true,
    },
  );

  const { run: addLabel } = useFetch(materialsCreateLabel, {
    manual: true,
    onSuccess: (data) => {
      setOpen(false);
      if (!data.error) {
        onAdd?.(data.data);
        if (inputVal) {
          setInputVal('');
        }
      } else {
        showErrorMsg(data.error);
      }
    },
  });

  const renderLabels = useMemo(() => {
    if (!inputVal.length) {
      return options;
    }
    return labels?.data?.labels;
  }, [inputVal, labels, options]);

  const handleCompositionStart = () => {
    isComposing.current = true;
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    isComposing.current = false;
    // 模拟 onChange 事件的处理逻辑
    setInputVal((e.target as EventTarget & HTMLInputElement)?.value);
    getLabels({
      query: {
        name_like: (e.target as EventTarget & HTMLInputElement).value,
      },
    });
  };

  useEffect(() => {
    const onResize = (event: Event) => {
      event.stopImmediatePropagation();
    };
    window.addEventListener('resize', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize, true);
    };
  }, []);

  return (
    <Select
      value={value}
      open={open}
      onValueChange={(e) => {
        setInputVal('');
        onChange?.(e);
        mutate();
      }}
      onOpenChange={(open) => {
        setOpen(open);
        if (open) {
          !value &&
            setTimeout(() => {
              ref.current?.focus();
            }, 10);
        } else {
          setInputVal('');
          mutate();
          isComposing.current = false;
          if (ref.current) {
            ref.current.value = '';
          }
        }
      }}
    >
      <SelectTrigger
        className="h-11 w-full border-none bg-system-black36 py-3 text-system-white36"
        onKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <div>
          <SelectValue placeholder={placeholder} />
        </div>
      </SelectTrigger>
      <SelectContent
        container={document.getElementById('element-main')}
        sideOffset={6}
        className="max-h-[var(--radix-select-content-available-height)] w-[var(--radix-select-trigger-width)] px-0 py-0"
        position="popper"
        side="top"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
        onKeyDown={(e) => {
          e.preventDefault();
        }}
      >
        <div className="flex flex-col-reverse">
          <div
            className="relative mx-3 flex items-center border-t-[1px] border-t-system-white08"
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              placeholder={t('elementDialog.tagCreatePlaceholder')}
              className="h-11 flex-1 border-none bg-system-bg02 px-0"
              ref={ref}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!isComposing.current) {
                  getLabels({
                    query: {
                      name_like: e.target.value,
                    },
                  });
                  setInputVal(e.target?.value ?? '');
                }
              }}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              onBlur={(_e) => {
                ref.current?.focus();
              }}
              onKeyDown={async (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key === 'Enter' && inputVal) {
                  addLabel({
                    body: {
                      name: inputVal,
                    },
                  });
                  setInputVal('');
                }
              }}
              maxLength={TAG_NAME_MAX_LENGTH}
            />
            {labelLoading && <Loading className="flex-shrink-0 animate-spin" />}
            <div className="ml-1 flex flex-shrink-0 items-center text-sm text-system-white36">
              <div className={cn(inputVal?.length && 'text-white', 'mr-1')}>{inputVal?.length || 0}</div>/
              {TAG_NAME_MAX_LENGTH}
            </div>
          </div>
          {!labelLoading && inputVal?.length && !labels?.data?.labels?.length ? (
            <div
              className="m-1 flex rounded-6 bg-system-hover01 p-2 text-xs"
              onClick={() => {
                addLabel({
                  body: {
                    name: inputVal,
                  },
                });
                setInputVal('');
              }}
            >
              <div className="mr-2 opacity-40">{t('elementDialog.createTag')}</div>
              <div>{inputVal}</div>
            </div>
          ) : null}
          {renderLabels?.length && !labelLoading ? (
            <SelectGroup
              className={cn(labelLoading && 'hidden', 'hide-scrollbar max-h-[148px] overflow-y-scroll px-1.5 py-2')}
            >
              {/* biome-ignore lint/suspicious/noExplicitAny: <explanation></explanation> */}
              {renderLabels.map((it: any) => (
                <SelectItem className="w-full px-1.5 py-1" value={it.id} key={it.id} data-state={'unchecked'}>
                  <TagItem
                    name={it.name}
                    isSelected={false}
                    size="small"
                    className="w-fit"
                    innerClassName="sm:text-xs"
                  />
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null}
        </div>
      </SelectContent>
    </Select>
  );
});
